import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { format, parseISO } from "date-fns";
import React, { useMemo } from "react";

import { ChatInput } from "../../..";
import { DataLoadingIndicator } from "./DataLoadingIndicator";

import type { Session } from "../../../../types";

type LoadingStatus = "loading" | "success" | "error" | null;

export interface FailedSession {
  sessionId: string;
  error?: string;
  orgPlayerId?: string;
  sessionDate?: string;
}

interface ReportTypeStepProps {
  inputValue: string;
  setInputValue: (value: string) => void;
  onSend: () => void;
  onVoiceTranscript: (transcript: string) => void;
  loadingStatus?: LoadingStatus;
  loadingProgress?: number;
  failedUploads?: FailedSession[];
  failedSessions?: FailedSession[];
  succeededSessions?: string[];
  availableSessions?: Session[];
  onRetry?: () => void;
  fileIds?: string[];
}

export const ReportTypeStep: React.FC<ReportTypeStepProps> = ({
  inputValue,
  setInputValue,
  onSend,
  onVoiceTranscript,
  loadingStatus = null,
  loadingProgress = 0,
  failedUploads = [],
  failedSessions = [],
  succeededSessions = [],
  availableSessions = [],
  onRetry,
  fileIds = [],
}) => {
  // Map failed sessions with their dates from available sessions
  const failedSessionsWithDates = useMemo(() => {
    return failedSessions.map((failedSession) => {
      const session = availableSessions.find(
        (s) => s.id === failedSession.sessionId,
      );
      return {
        ...failedSession,
        sessionDate: session?.sessionDate || failedSession.sessionDate,
      };
    });
  }, [failedSessions, availableSessions]);

  // Only count succeeded if files were actually created (guard against API reporting succeeded_sessions but upload having failed)
  const hasActualSuccess = succeededSessions.length > 0 && fileIds.length > 0;

  const hasStatusMessages =
    loadingStatus === "loading" ||
    loadingStatus === "error" ||
    failedUploads.length > 0 ||
    failedSessions.length > 0 ||
    hasActualSuccess;

  return (
    <div className="box-border content-stretch flex flex-col gap-[24px] items-center pb-0 pt-[12px] px-0 relative shrink-0 w-full max-w-[960px]">
      {/* Status Indicators */}
      {hasStatusMessages && (
        <div className="content-stretch flex flex-col gap-[12px] items-center relative shrink-0 w-full max-w-[842px]">
          {/* Loading State */}
          {loadingStatus === "loading" && (
            <div
              data-testid="reboot-live-data-loading-indicator"
              className="bg-[#1d1d1f] h-[44px] relative rounded-[8px] shrink-0 w-full"
            >
              <div
                aria-hidden="true"
                className="absolute border-[0.833px] border-[rgba(255,255,255,0.1)] border-solid inset-0 pointer-events-none rounded-[8px]"
              />
              <div className="bg-clip-padding border-0 border-transparent border-solid box-border content-stretch flex flex-col h-[44px] items-start justify-center px-[16px] py-0 relative w-full">
                <div className="content-stretch flex h-[48px] items-center justify-between relative shrink-0 w-full">
                  <div className="h-[48px] relative shrink-0">
                    <div className="bg-clip-padding border-0 border-transparent border-solid box-border content-stretch flex flex-col gap-0 h-[48px] items-start justify-center relative">
                      <div className="h-[24px] relative shrink-0 w-[132px]">
                        <p className="absolute font-normal leading-[24px] left-0 not-italic text-[16px] text-nowrap text-white top-0 tracking-[-0.3125px] whitespace-pre">
                          Loading data...
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="h-full relative shrink-0">
                    <div className="bg-clip-padding border-0 border-transparent border-solid box-border content-stretch flex gap-0 h-full items-center justify-end relative">
                      <DataLoadingIndicator percentage={loadingProgress} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Failed Uploads */}
          {failedUploads.length > 0 && (
            <div className="bg-[#1d1d1f] h-[44px] relative rounded-[8px] shrink-0 w-full">
              <div
                aria-hidden="true"
                className="absolute border-[0.833px] border-[rgba(255,255,255,0.1)] border-solid inset-0 pointer-events-none rounded-[8px]"
              />
              <div className="bg-clip-padding border-0 border-transparent border-solid box-border content-stretch flex h-[44px] items-center justify-between px-[16px] py-0 relative w-full">
                <div className="basis-0 content-stretch flex gap-[12px] grow items-center min-h-px min-w-px relative shrink-0">
                  <div className="relative shrink-0 size-[20px]">
                    <XCircleIcon className="size-[20px] text-[#F87171]" />
                  </div>
                  <div className="h-full relative shrink-0">
                    <div className="bg-clip-padding border-0 border-transparent border-solid box-border content-stretch flex flex-col gap-0 h-full items-start justify-center relative">
                      <p className="font-normal leading-[24px] not-italic relative shrink-0 text-[14px] text-nowrap text-white tracking-[-0.3125px] whitespace-pre">
                        {failedUploads.length}{" "}
                        {failedUploads.length === 1 ? "Session" : "Sessions"}{" "}
                        failed to upload
                      </p>
                    </div>
                  </div>
                </div>
                {onRetry && (
                  <div className="relative shrink-0">
                    <button onClick={onRetry} className="cursor-pointer">
                      <p className="font-normal leading-[24px] not-italic relative shrink-0 text-[#fb6a07] text-[14px] text-nowrap tracking-[-0.3125px] whitespace-pre">
                        retry
                      </p>
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Failed Sessions */}
          {failedSessions.length > 0 && (
            <div className="bg-[#1d1d1f] h-[44px] relative rounded-[8px] shrink-0 w-full overflow-visible">
              <div
                aria-hidden="true"
                className="absolute border-[0.833px] border-[rgba(255,255,255,0.1)] border-solid inset-0 pointer-events-none rounded-[8px]"
              />
              <div className="bg-clip-padding border-0 border-transparent border-solid box-border content-stretch flex h-[44px] items-center justify-between px-[16px] py-0 relative w-full">
                <div className="basis-0 content-stretch flex gap-[12px] grow items-center min-h-px min-w-px relative shrink-0">
                  <div className="relative shrink-0 size-[20px]">
                    <XCircleIcon className="size-[20px] text-[#F87171]" />
                  </div>
                  <div className="h-full relative shrink-0">
                    <div className="bg-clip-padding border-0 border-transparent border-solid box-border content-stretch flex flex-col gap-0 h-full items-start justify-center relative">
                      <p className="font-normal leading-[24px] not-italic relative shrink-0 text-[14px] text-nowrap text-white tracking-[-0.3125px] whitespace-pre">
                        {failedSessions.length}{" "}
                        {failedSessions.length === 1 ? "Session" : "Sessions"}{" "}
                        failed because no data was found for the date(s) and
                        movement type.
                      </p>
                    </div>
                  </div>
                </div>
                {failedSessions.length > 0 && (
                  <div className="relative shrink-0">
                    <Popover>
                      <PopoverButton
                        data-testid="reboot-live-failed-sessions-more-info-button"
                        className="cursor-pointer focus:outline-none"
                      >
                        <p className="font-normal leading-[24px] not-italic relative shrink-0 text-[#fb6a07] text-[14px] text-nowrap tracking-[-0.3125px] whitespace-pre">
                          More info
                        </p>
                      </PopoverButton>
                      <PopoverPanel
                        data-testid="reboot-live-failed-sessions-popover"
                        className="absolute z-50 mb-2 bottom-full right-[-34px] bg-[#1D1D1F] rounded-[6px] w-[288px]"
                      >
                        <div
                          aria-hidden="true"
                          className="absolute border-[0.833px] border-[rgba(255,255,255,0.1)] border-solid inset-0 pointer-events-none rounded-[6px]"
                        />
                        <div className="bg-clip-padding border-0 border-transparent border-solid box-border content-stretch flex flex-col items-start p-[16px] relative w-full">
                          {/* Header */}
                          <div className="relative shrink-0 w-full mb-[12px]">
                            <p
                              data-testid="reboot-live-failed-sessions-popover-header"
                              className="font-medium not-italic relative shrink-0 text-[#7E7E81] text-[12px] leading-[20px] tracking-[0%]"
                            >
                              Session ID - Session Date
                            </p>
                          </div>
                          {/* Failed Sessions List */}
                          <div
                            data-testid="reboot-live-failed-sessions-list"
                            className="content-stretch flex flex-col gap-[8px] items-start relative shrink-0 w-full"
                          >
                            {failedSessionsWithDates.map(
                              (failedSession, index) => {
                                let formattedDate = "0/0/0000";
                                if (failedSession.sessionDate) {
                                  try {
                                    const date = parseISO(
                                      failedSession.sessionDate,
                                    );
                                    formattedDate = format(date, "M/d/yyyy");
                                  } catch {
                                    // Keep default if parsing fails
                                  }
                                }
                                const truncatedSessionId =
                                  failedSession.sessionId.length > 16
                                    ? `${failedSession.sessionId.substring(0, 16)}...`
                                    : failedSession.sessionId;
                                const fullText = `${failedSession.sessionId} - ${formattedDate}`;

                                return (
                                  <div
                                    key={`${failedSession.sessionId}-${index}`}
                                    data-testid={`reboot-live-failed-session-item-${index}`}
                                    className="content-stretch flex gap-[8px] items-center relative shrink-0 w-full min-w-0"
                                  >
                                    <div className="relative shrink-0 size-[16px]">
                                      <XCircleIcon className="size-[16px] text-[#F87171]" />
                                    </div>
                                    <p
                                      className="font-normal leading-[20px] not-italic relative shrink-0 text-[14px] text-white truncate min-w-0 flex-1"
                                      title={fullText}
                                    >
                                      {truncatedSessionId} - {formattedDate}
                                    </p>
                                  </div>
                                );
                              },
                            )}
                          </div>
                        </div>
                      </PopoverPanel>
                    </Popover>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Succeeded Sessions - only show if files were actually created */}
          {succeededSessions.length > 0 && fileIds.length > 0 && (
            <div className="bg-[#1d1d1f] h-[44px] relative rounded-[8px] shrink-0 w-full">
              <div
                aria-hidden="true"
                className="absolute border-[0.833px] border-[rgba(255,255,255,0.1)] border-solid inset-0 pointer-events-none rounded-[8px]"
              />
              <div className="bg-clip-padding border-0 border-transparent border-solid box-border content-stretch flex h-[44px] items-center justify-center px-[16px] py-0 relative w-full">
                <div className="basis-0 content-stretch flex gap-[12px] grow items-center min-h-px min-w-px relative shrink-0">
                  <div className="relative shrink-0 size-[20px]">
                    <CheckCircleIcon className="size-[20px] text-[#20C997]" />
                  </div>
                  <div className="h-full relative shrink-0">
                    <div className="bg-clip-padding border-0 border-transparent border-solid box-border content-stretch flex flex-col gap-0 h-full items-start justify-center relative">
                      <p className="font-normal leading-[24px] not-italic relative shrink-0 text-[14px] text-nowrap text-white tracking-[-0.3125px] whitespace-pre">
                        {succeededSessions.length}{" "}
                        {succeededSessions.length === 1
                          ? "Session"
                          : "Sessions"}{" "}
                        loaded successfully
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Fallback Error State (no failedUploads or failedSessions, but error status) */}
          {loadingStatus === "error" &&
            failedUploads.length === 0 &&
            failedSessions.length === 0 &&
            succeededSessions.length === 0 && (
              <div
                data-testid="reboot-live-api-error-indicator"
                className="bg-[#1d1d1f] h-[44px] relative rounded-[8px] shrink-0 w-full"
              >
                <div
                  aria-hidden="true"
                  className="absolute border-[0.833px] border-[rgba(255,255,255,0.1)] border-solid inset-0 pointer-events-none rounded-[8px]"
                />
                <div className="bg-clip-padding border-0 border-transparent border-solid box-border content-stretch flex h-[44px] items-center justify-between px-[16px] py-0 relative w-full">
                  <div className="basis-0 content-stretch flex gap-[12px] grow items-center min-h-px min-w-px relative shrink-0">
                    <div className="relative shrink-0 size-[20px]">
                      <XCircleIcon className="size-[20px] text-[#F87171]" />
                    </div>
                    <div className="basis-0 grow h-full min-h-px min-w-px relative shrink-0">
                      <div className="bg-clip-padding border-0 border-transparent border-solid box-border content-stretch flex gap-[10px] items-center relative size-full">
                        <p className="font-normal leading-[24px] not-italic relative shrink-0 text-[14px] text-nowrap text-white tracking-[-0.3125px] whitespace-pre">
                          Failed to load data. Please try again.
                        </p>
                      </div>
                    </div>
                  </div>
                  {onRetry && (
                    <div className="relative shrink-0">
                      <button
                        data-testid="reboot-live-api-error-retry-button"
                        onClick={onRetry}
                        className="cursor-pointer"
                      >
                        <p className="font-normal leading-[24px] not-italic relative shrink-0 text-[#fb6a07] text-[14px] text-nowrap tracking-[-0.3125px] whitespace-pre">
                          retry
                        </p>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
        </div>
      )}
      <p
        data-testid="reboot-live-report-type-prompt"
        className="font-medium leading-[23px] not-italic relative shrink-0 text-[18px] text-center text-white"
      >
        Describe what kind of report you&apos;d like.
      </p>
      <div className="content-stretch flex flex-col gap-[21px] items-center justify-center relative shrink-0 w-full">
        <ChatInput
          inputValue={inputValue}
          setInputValue={setInputValue}
          onSend={onSend}
          onVoiceTranscript={onVoiceTranscript}
          sendDisabled={loadingStatus === "loading" || fileIds.length === 0}
          placeholder="Describe what you want to learn from this report and your preferred format (e.g. HTML). Include specific topics, comparisons, trends, or questions you want answered."
        />
      </div>
    </div>
  );
};
